/**
 * Canvas Data Prefetch
 *
 * Warms library model cache on canvas load so the settings panel
 * opens instantly without loading spinners.
 *
 * Strategy: Prefetch library models per node type on canvas init.
 * Library is small (<100 models), so a single call per node type is sufficient.
 */

import type { AINodeType, AiModelPurpose } from '@genieartinc/schemas'
import { warmLibraryModelsCache } from '@/hooks/useLibraryModels'
import { warmModelCache } from '@/hooks/useNodeModels'

/** All AI node types that use the model settings panel */
const AI_NODE_TYPES: AINodeType[] = ['text-llm', 'image', 'video', 'sound', '3d']

/** Node types that have purpose variants */
const PURPOSE_NODE_TYPES: AINodeType[] = ['image', 'video', '3d']
const PURPOSES: AiModelPurpose[] = ['create', 'edit', 'enhance', 'tools']

let prefetchStarted = false

/**
 * Prefetch library models for all node types and purposes on canvas load.
 * Warms purpose-specific caches so first click on any node shows settings instantly.
 * Runs once per session — safe to call multiple times.
 */
export function prefetchLibraryModels(): void {
  if (prefetchStarted) return
  prefetchStarted = true

  for (const nodeType of AI_NODE_TYPES) {
    warmLibraryModelsCache(nodeType)
    warmModelCache(nodeType)

    // Purpose-specific prefetch for node types that have variants
    if (PURPOSE_NODE_TYPES.includes(nodeType)) {
      for (const purpose of PURPOSES) {
        warmModelCache(nodeType, undefined, purpose)
      }
    }
  }
}
